import { PlmError, type PlmAssembly, type PlmClient, type PlmInfo, type PlmPart } from './contract'

/**
 * Der Client — echtes HTTP gegen eine echte Adresse.
 *
 * WARUM NICHT EINFACH EIN IMPORT. Der Datensatz liegt heute im Repository und
 * liesse sich importieren; dann wäre die Anbindung aber keine Anbindung,
 * sondern eine zweite Tabelle im Bündel. Ein echtes PLM anzuschliessen hiesse
 * danach, den Weg von der Datei zum Netz überhaupt erst zu bauen — und zwar an
 * dem Tag, an dem die Zugangsdaten kommen und niemand mehr Zeit dafür hat.
 *
 * Deshalb spricht der Client von Anfang an HTTP. Die Basisadresse steht in
 * `VITE_PLM_BASE_URL`; ohne sie zeigt sie auf `/plm` im eigenen Auslieferstand,
 * wo der simulierte Datensatz liegt. Ein echtes PLM ist ein anderer Wert in
 * einer Umgebungsvariablen und sonst nichts.
 *
 * DIE SIMULATION IST ALS SOLCHE ERKENNBAR. `info().simulated` sagt es, und die
 * Baugruppe trägt es in `source` im Klartext. Eine Anbindung, der man nicht
 * ansieht, ob sie echte Daten liefert, ist die gefährlichere Sorte Platzhalter.
 *
 * WAS DER CLIENT NICHT TUT: rechnen. Er prüft die Antwort auf Form und gibt sie
 * weiter. Was daraus für die Physik folgt, steht in `rollup.ts`.
 */

/** Kein Schreibzugriff, keine Zugangsdaten — der Client liest. */
export interface PlmHttpOptions {
  /** Basisadresse ohne Schrägstrich am Ende. */
  baseUrl?: string
  /** Für Tests und für einen späteren Adapter mit Anmeldung. */
  fetch?: typeof globalThis.fetch
  /** Abbruch nach dieser Zeit, ms. */
  timeout?: number
}

const DEFAULT_BASE = '/plm'
const DEFAULT_TIMEOUT = 8000

/**
 * Woran man den simulierten Stand erkennt.
 *
 * Eine relative Adresse zeigt in den eigenen Auslieferstand — dort kann kein
 * PLM liegen. Alles mit einem Schema davor gilt als echte Gegenstelle.
 */
export const isSimulated = (baseUrl: string): boolean => !/^https?:\/\//i.test(baseUrl)

export class PlmHttpClient implements PlmClient {
  readonly baseUrl: string
  private readonly call: typeof globalThis.fetch
  private readonly timeout: number

  constructor(options: PlmHttpOptions = {}) {
    this.baseUrl = (options.baseUrl ?? DEFAULT_BASE).replace(/\/+$/, '')
    this.call = options.fetch ?? globalThis.fetch.bind(globalThis)
    this.timeout = options.timeout ?? DEFAULT_TIMEOUT
  }

  async info(): Promise<PlmInfo> {
    const raw = await this.get<{ assemblies?: unknown }>('/index.json')
    const list = Array.isArray(raw.assemblies) ? raw.assemblies : []
    return {
      endpoint: this.baseUrl,
      simulated: isSimulated(this.baseUrl),
      assemblies: list.map((entry) => {
        const row = entry as Record<string, unknown>
        return {
          id: String(row.id ?? ''),
          label: String(row.label ?? row.id ?? ''),
          revision: String(row.revision ?? ''),
        }
      }),
    }
  }

  async assembly(id: string): Promise<PlmAssembly> {
    /* Die Kennung wandert in einen Pfad. Ohne diese Prüfung liesse sich mit
       einem `../` aus dem Verzeichnis der Baugruppen heraussteuern. */
    if (!/^[a-zA-Z0-9._-]+$/.test(id)) {
      throw new PlmError(`Unzulässige Kennung: ${id}`, 'invalid')
    }
    return parseAssembly(await this.get<unknown>(`/assembly/${id}.json`), id)
  }

  private async get<T>(path: string): Promise<T> {
    const url = `${this.baseUrl}${path}`
    const control = new AbortController()
    const timer = setTimeout(() => control.abort(), this.timeout)
    try {
      const response = await this.call(url, {
        signal: control.signal,
        headers: { accept: 'application/json' },
      })
      if (response.status === 404) throw new PlmError(`Nicht gefunden: ${url}`, 'notfound')
      if (!response.ok) {
        throw new PlmError(`${url} antwortet mit ${response.status}`, 'unreachable')
      }
      return (await response.json()) as T
    } catch (error) {
      if (error instanceof PlmError) throw error
      if (error instanceof SyntaxError) {
        throw new PlmError(`${url} liefert kein JSON`, 'invalid')
      }
      throw new PlmError(`${url} ist nicht erreichbar: ${(error as Error).message}`, 'unreachable')
    } finally {
      clearTimeout(timer)
    }
  }
}

/* -------------------------------------------------------------------------
   Die Antwort prüfen
   ------------------------------------------------------------------------- */

/**
 * Aus einer Antwort wird eine Baugruppe — oder ein Fehler.
 *
 * EINE FREMDE ANTWORT WIRD GEPRÜFT UND NICHT GEGLAUBT. Der Rechenkern teilt
 * durch Massen und zieht Wurzeln aus Trägheiten; ein fehlendes Feld wird dort
 * zu `NaN` und läuft still durch die halbe Simulation. Der Bruch gehört an die
 * Grenze, an der die Daten hereinkommen.
 */
export function parseAssembly(raw: unknown, id: string): PlmAssembly {
  if (!raw || typeof raw !== 'object') throw new PlmError('Antwort ist kein Objekt', 'invalid')
  const row = raw as Record<string, unknown>
  const parts = Array.isArray(row.parts) ? row.parts : null
  if (!parts) throw new PlmError('Antwort führt keine Teileliste', 'invalid')

  return {
    id: String(row.id ?? id),
    label: String(row.label ?? id),
    revision: String(row.revision ?? '—'),
    releasedAt: String(row.releasedAt ?? ''),
    source: String(row.source ?? 'unbekannt'),
    parts: parts.map((entry, index) => parsePart(entry, index)),
  }
}

const STATES = new Set(['entwurf', 'in_pruefung', 'freigegeben'])

function parsePart(raw: unknown, index: number): PlmPart {
  if (!raw || typeof raw !== 'object') {
    throw new PlmError(`Teil ${index} ist kein Objekt`, 'invalid')
  }
  const row = raw as Record<string, unknown>
  const at = (key: string): string => `Teil ${index} (${String(row.number ?? '?')}) · ${key}`

  const number = (key: string): number => {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new PlmError(`${at(key)} ist keine Zahl`, 'invalid')
    }
    return value
  }

  const vector = (key: string) => {
    const value = row[key] as Record<string, unknown> | undefined
    if (!value || typeof value !== 'object') throw new PlmError(`${at(key)} fehlt`, 'invalid')
    const axis = (name: 'x' | 'y' | 'z') => {
      const component = value[name]
      if (typeof component !== 'number' || !Number.isFinite(component)) {
        throw new PlmError(`${at(`${key}.${name}`)} ist keine Zahl`, 'invalid')
      }
      return component
    }
    return { x: axis('x'), y: axis('y'), z: axis('z') }
  }

  const inertiaRaw = row.inertia as Record<string, unknown> | undefined
  if (!inertiaRaw || typeof inertiaRaw !== 'object') {
    throw new PlmError(`${at('inertia')} fehlt`, 'invalid')
  }
  const component = (name: 'xx' | 'yy' | 'zz') => {
    const value = inertiaRaw[name]
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
      throw new PlmError(`${at(`inertia.${name}`)} ist keine gültige Trägheit`, 'invalid')
    }
    return value
  }

  const mass = number('mass')
  if (mass <= 0) throw new PlmError(`${at('mass')} ist nicht positiv`, 'invalid')

  const state = String(row.state ?? 'entwurf')
  if (!STATES.has(state)) throw new PlmError(`${at('state')} ist unbekannt: ${state}`, 'invalid')

  const side = row.side === null || row.side === undefined ? null : String(row.side)
  if (side !== null && side !== 'R' && side !== 'L') {
    throw new PlmError(`${at('side')} ist weder R noch L`, 'invalid')
  }

  return {
    number: String(row.number ?? `?${index}`),
    revision: String(row.revision ?? '—'),
    name: String(row.name ?? ''),
    segment: String(row.segment ?? '') as PlmPart['segment'],
    side,
    mass,
    cog: vector('cog'),
    inertia: { xx: component('xx'), yy: component('yy'), zz: component('zz') },
    material: String(row.material ?? '—'),
    state: state as PlmPart['state'],
  }
}
